<template>
  <Toast position="bottom-center" />
  <Toast group="push" position="bottom-center" />

  <Dialog
    v-model:visible="appDialogsStore.applicationUpdatedOpen"
    :modal="true"
    :closable="false"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog"
    :style="{ width: 'min(36rem, calc(100vw - 2rem))', borderRadius: '10px !important' }"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ t("wkt.client.application.application_updated.title") }}
        </h3>
      </div>
    </template>

    <div>
      <p class="app-global-dialog__subtitle">
        {{ t("wkt.client.application.application_updated.title_description") }}
      </p>
    </div>


    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__right">
          <Button
            :label="t('wkt.client.application.application_updated.update')"
            class="dialog-action--primary"
            @click="reloadApplication"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="appDialogsStore.confirmDialogOpen"
    :modal="true"
    :draggable="false"
    :closable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog"
    :style="{ width: 'min(34rem, calc(100vw - 2rem))' }"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ appDialogsStore.confirmDialogTitle }}
        </h3>
      </div>
    </template>

    <div class="app-global-dialog__body">
      <p class="app-global-dialog__copy">
        {{ appDialogsStore.confirmDialogMessage }}
      </p>

      <div
        v-if="requiresConfirmToken"
        class="app-global-dialog__confirm-token"
      >
        <label class="app-global-dialog__confirm-label">
          {{ appDialogsStore.confirmDialogInstruction }}
        </label>
        <InputText
          v-model="confirmDialogInput"
          class="app-global-dialog__input"
          @input="confirmDialogInputError = ''"
        />
        <Message v-if="confirmDialogInputError" severity="error" :closable="false">
          {{ confirmDialogInputError }}
        </Message>
      </div>
    </div>

    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__right">
          <Button
            :label="appDialogsStore.confirmDialogCancelLabel || t('wkt.client.common.dialog-buttons.cancel')"
            class="dialog-action--cancel"
            text
            @click="cancelGlobalDialog"
          />
          <Button
            :label="appDialogsStore.confirmDialogAcceptLabel || t('wkt.client.common.dialog-buttons.ok')"
            class="dialog-action--primary"
            @click="confirmGlobalDialog"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="appDialogsStore.logoutOpen"
    :modal="true"
    :closable="false"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog"
    :style="{ width: 'min(28rem, calc(100vw - 2rem))', minHeight: '240px !important', borderRadius: '10px !important' }"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ t("wkt.client.application.logout.title") }}
        </h3>
      </div>
    </template>

    <div class="app-global-dialog__body">
      <p class="app-global-dialog__copy">
        {{ currentEmployeeName }}
      </p>
    </div>

    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__right">
          <Button
            :label="t('wkt.client.common.dialog-buttons.cancel')"
            class="dialog-action--cancel"
            text
            @click="appDialogsStore.closeLogout()"
          />
          <Button
            :label="t('wkt.client.application.logout.exit')"
            class="dialog-action--primary"
            @click="confirmLogout"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="appDialogsStore.directMessageOpen"
    :modal="true"
    :closable="false"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog app-global-dialog--direct-message"
    :style="{ width: 'min(40rem, calc(100vw - 2rem))' }"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ t("wkt.client.application.message-window.window_title_new") }}
        </h3>
      </div>
    </template>

    <div class="app-global-dialog__body">
      <div class="app-global-dialog__recipient">
        <img
          v-if="directMessageRecipientAvatarUrl"
          :src="directMessageRecipientAvatarUrl"
          alt=""
          class="app-global-dialog__recipient-avatar"
        />
        <div class="app-global-dialog__recipient-copy">
          <strong>{{ directMessageRecipientName }}</strong>
          <span v-if="appDialogsStore.directMessageRecipientDepartmentName">
            {{ appDialogsStore.directMessageRecipientDepartmentName }}
          </span>
        </div>
      </div>

      <div class="app-global-dialog__field">
        <label class="app-global-dialog__confirm-label">
          {{ t("wkt.client.application.message-window.message_title") }}
        </label>
        <InputText
          :model-value="appDialogsStore.directMessageTitle"
          class="app-global-dialog__input"
          @update:model-value="onDirectMessageTitleUpdate"
        />
        <Message v-if="directMessageTitleError" severity="error" :closable="false">
          {{ directMessageTitleError }}
        </Message>
      </div>

      <div class="app-global-dialog__field">
        <label class="app-global-dialog__confirm-label">
          {{ t("wkt.client.application.message-window.message_content") }}
        </label>
        <Textarea
          :model-value="appDialogsStore.directMessageBody"
          rows="6"
          auto-resize
          class="app-global-dialog__textarea"
          @update:model-value="onDirectMessageBodyUpdate"
        />
        <Message v-if="directMessageBodyError" severity="error" :closable="false">
          {{ directMessageBodyError }}
        </Message>
      </div>
    </div>

    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__right">
          <Button
            :label="t('wkt.client.common.dialog-buttons.cancel')"
            class="dialog-action--cancel"
            text
            :disabled="directMessageSubmitting"
            @click="closeDirectMessage"
          />
          <Button
            :label="t('wkt.client.common.dialog-buttons.send')"
            class="dialog-action--primary"
            :loading="directMessageSubmitting"
            @click="submitDirectMessage"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="appDialogsStore.incidenceOpen"
    :modal="true"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog"
    :style="{ width: 'min(42rem, calc(100vw - 2rem))' }"
    @hide="appDialogsStore.closeIncidence()"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ t("wkt.client.application.send-incidence-window.title") }}
        </h3>
        <p class="app-global-dialog__subtitle">
          {{ t("wkt.client.application.send-incidence-window.description") }}
        </p>
      </div>
    </template>

    <div class="app-global-dialog__body">
      <Textarea
        :model-value="appDialogsStore.incidenceDraft"
        rows="6"
        auto-resize
        class="app-global-dialog__textarea"
        :placeholder="t('wkt.client.application.send-incidence-window.description')"
        @update:model-value="onIncidenceDraftUpdate"
      />
      <Message v-if="incidenceError" severity="error" :closable="false">
        {{ incidenceError }}
      </Message>
    </div>

    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__right">
          <Button
            :label="t('wkt.client.common.dialog-buttons.cancel')"
            class="dialog-action--cancel"
            text
            :disabled="appDialogsStore.incidenceSubmitting"
            @click="appDialogsStore.closeIncidence()"
          />
          <Button
            :label="t('wkt.client.common.dialog-buttons.send')"
            class="dialog-action--primary"
            :loading="appDialogsStore.incidenceSubmitting"
            @click="submitIncidence"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="appDialogsStore.pendingTermsOpen"
    :modal="true"
    :closable="false"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog app-global-dialog--fullscreen"
    :style="{ width: 'min(60rem, calc(100vw - 2rem))' }"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ currentPendingTerm?.title || t("wkt.client.profile.view_accepted_terms") }}
        </h3>
        <p v-if="pendingTermsPagerText" class="app-global-dialog__subtitle">
          {{ pendingTermsPagerText }}
        </p>
      </div>
    </template>

    <div class="app-global-dialog__body">
      <div
        v-if="currentPendingTerm?.terms"
        class="app-global-dialog__html"
        v-html="currentPendingTerm.terms"
      />
    </div>

    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__right">
          <Button
            :label="t('wkt.client.common.dialog-buttons.cancel')"
            class="dialog-action--cancel"
            text
            :disabled="appDialogsStore.pendingTermsSubmitting"
            @click="rejectPendingTerms"
          />
          <Button
            :label="t('wkt.client.common.dialog-buttons.ok')"
            class="dialog-action--primary"
            :loading="appDialogsStore.pendingTermsSubmitting"
            @click="acceptCurrentPendingTerm"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="appDialogsStore.featureMessagesOpen"
    :modal="true"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog app-global-dialog--fullscreen"
    :style="{ width: 'min(54rem, calc(100vw - 2rem))' }"
    @hide="closeFeatureMessages"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ t("wkt.client.profile.view_features") }}
        </h3>
        <p v-if="featurePagerText" class="app-global-dialog__subtitle">
          {{ featurePagerText }}
        </p>
      </div>
    </template>

    <div class="app-global-dialog__body">
      <div v-if="currentFeatureMessage" class="app-global-dialog__article">
        <h4 class="app-global-dialog__article-title">
          {{ currentFeatureMessage.messageTitle }}
        </h4>
        <div
          class="app-global-dialog__html"
          v-html="currentFeatureMessage.messageBody || ''"
        />
      </div>
    </div>

    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__left">
          <Button
            v-if="hasPrevFeatureMessage"
            :label="t('wkt.client.common.dialog-buttons.prev')"
            class="dialog-action--secondary"
            text
            @click="appDialogsStore.prevFeatureMessage()"
          />
          <Button
            v-if="hasNextFeatureMessage"
            :label="t('wkt.client.common.dialog-buttons.next')"
            class="dialog-action--secondary"
            text
            @click="appDialogsStore.nextFeatureMessage()"
          />
        </div>
        <div class="dialog-actions__right">
          <Button
            :label="t('wkt.client.common.dialog-buttons.close')"
            class="dialog-action--cancel"
            text
            @click="closeFeatureMessages"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="appDialogsStore.acceptedTermsOpen"
    :modal="true"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop app-global-dialog app-global-dialog--fullscreen"
    :style="{ width: 'min(60rem, calc(100vw - 2rem))' }"
    @hide="appDialogsStore.closeAcceptedTerms()"
  >
    <template #header>
      <div class="app-global-dialog__header">
        <h3 class="app-global-dialog__title">
          {{ currentAcceptedTerm?.title || t("wkt.client.profile.view_accepted_terms") }}
        </h3>
        <p v-if="acceptedTermsPagerText" class="app-global-dialog__subtitle">
          {{ acceptedTermsPagerText }}
        </p>
      </div>
    </template>

    <div class="app-global-dialog__body">
      <div
        v-if="currentAcceptedTerm?.terms"
        class="app-global-dialog__html"
        v-html="currentAcceptedTerm.terms"
      />
    </div>

    <template #footer>
      <div class="dialog-actions">
        <div class="dialog-actions__left">
          <Button
            v-if="hasPrevAcceptedTerm"
            :label="t('wkt.client.common.dialog-buttons.prev')"
            class="dialog-action--secondary"
            text
            @click="appDialogsStore.prevAcceptedTerm()"
          />
          <Button
            v-if="hasNextAcceptedTerm"
            :label="t('wkt.client.common.dialog-buttons.next')"
            class="dialog-action--secondary"
            text
            @click="appDialogsStore.nextAcceptedTerm()"
          />
        </div>
        <div class="dialog-actions__right">
          <Button
            :label="t('wkt.client.common.dialog-buttons.close')"
            class="dialog-action--cancel"
            text
            @click="appDialogsStore.closeAcceptedTerms()"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useCordovaBridge } from "@wkt/composables/useCordovaBridge";
import { isAppRuntime } from "@wkt/lib/device";
import { homeApi } from "@wkt/api/home/homeAPI";
import { userApi } from "@wkt/api/user/userAPI";
import { useAlert } from "@wkt/composables/useAlert";
import { useGlobalDialogActions } from "@wkt/composables/useGlobalDialogActions";
import { useUtils } from "@wkt/composables/useUtils";
import { useAppDialogsStore } from "@wkt/stores/appDialogs";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";

const CLIENT_MODULE = "CLIENT";

const { t } = useI18n();
const cordovaBridge = useCordovaBridge();
const { showAlert } = useAlert();
const { confirmLogout, openLogoutDialog } = useGlobalDialogActions();
const { getProfileAssetUrl } = useUtils();
const appDialogsStore = useAppDialogsStore();
const sessionCatalogStore = useSessionCatalogStore();

const incidenceError = ref("");
const confirmDialogInput = ref("");
const confirmDialogInputError = ref("");
const directMessageTitleError = ref("");
const directMessageBodyError = ref("");
const directMessageSubmitting = ref(false);

const currentEmployeeName = computed(() => {
  const employee = sessionCatalogStore.employee;
  if (!employee) {
    return "";
  }

  return [employee.firstname, employee.lastname].filter(Boolean).join(" ");
});

const currentFeatureMessage = computed(
  () => appDialogsStore.featureMessages[appDialogsStore.featureMessageIndex] ?? null,
);

const currentPendingTerm = computed(
  () => appDialogsStore.pendingTerms[appDialogsStore.pendingTermIndex] ?? null,
);

const hasNextPendingTerm = computed(
  () => appDialogsStore.pendingTermIndex < appDialogsStore.pendingTerms.length - 1,
);

const pendingTermsPagerText = computed(() => {
  if (appDialogsStore.pendingTerms.length <= 1) {
    return "";
  }

  return `${appDialogsStore.pendingTermIndex + 1} / ${appDialogsStore.pendingTerms.length}`;
});

const hasPrevFeatureMessage = computed(
  () => appDialogsStore.featureMessageIndex > 0,
);

const hasNextFeatureMessage = computed(
  () =>
    appDialogsStore.featureMessageIndex <
    appDialogsStore.featureMessages.length - 1,
);

const featurePagerText = computed(() => {
  if (appDialogsStore.featureMessages.length <= 1) {
    return "";
  }

  return `${appDialogsStore.featureMessageIndex + 1} / ${appDialogsStore.featureMessages.length}`;
});

const currentAcceptedTerm = computed(
  () => appDialogsStore.acceptedTerms[appDialogsStore.acceptedTermIndex] ?? null,
);

const hasPrevAcceptedTerm = computed(() => appDialogsStore.acceptedTermIndex > 0);

const hasNextAcceptedTerm = computed(
  () =>
    appDialogsStore.acceptedTermIndex < appDialogsStore.acceptedTerms.length - 1,
);

const acceptedTermsPagerText = computed(() => {
  if (appDialogsStore.acceptedTerms.length <= 1) {
    return "";
  }

  return `${appDialogsStore.acceptedTermIndex + 1} / ${appDialogsStore.acceptedTerms.length}`;
});

const requiresConfirmToken = computed(
  () => appDialogsStore.confirmDialogToken.trim().length > 0,
);

const directMessageRecipientName = computed(() =>
  [
    appDialogsStore.directMessageRecipientFirstname,
    appDialogsStore.directMessageRecipientLastname,
  ]
    .filter(Boolean)
    .join(" "),
);

const directMessageRecipientAvatarUrl = computed(() => {
  if (!appDialogsStore.directMessageRecipientProfilePicture) {
    return "";
  }

  return getProfileAssetUrl(appDialogsStore.directMessageRecipientProfilePicture);
});

function onIncidenceDraftUpdate(value: string | undefined): void {
  incidenceError.value = "";
  appDialogsStore.setIncidenceDraft(value ?? "");
}

function onDirectMessageTitleUpdate(value: string | undefined): void {
  directMessageTitleError.value = "";
  appDialogsStore.setDirectMessageTitle(value ?? "");
}

function onDirectMessageBodyUpdate(value: string | undefined): void {
  directMessageBodyError.value = "";
  appDialogsStore.setDirectMessageBody(value ?? "");
}

function reloadApplication(): void {
  appDialogsStore.closeApplicationUpdated();
  if (isAppRuntime()) {
    cordovaBridge.reloadApp();
  } else {
    window.location.reload();
  }
}

function cancelGlobalDialog(): void {
  const onCancel = appDialogsStore.confirmDialogOnCancel;
  appDialogsStore.closeConfirmDialog();
  confirmDialogInput.value = "";
  confirmDialogInputError.value = "";
  onCancel?.();
}

function closeDirectMessage(): void {
  appDialogsStore.closeDirectMessageDialog();
  directMessageTitleError.value = "";
  directMessageBodyError.value = "";
}

function confirmGlobalDialog(): void {
  if (requiresConfirmToken.value) {
    if (
      confirmDialogInput.value.trim() !==
      appDialogsStore.confirmDialogToken.trim()
    ) {
      confirmDialogInputError.value =
        appDialogsStore.confirmDialogError ||
        t("wkt.client.common.dialog-buttons.cancel");
      return;
    }
  }

  const onConfirm = appDialogsStore.confirmDialogOnConfirm;
  appDialogsStore.closeConfirmDialog();
  confirmDialogInput.value = "";
  confirmDialogInputError.value = "";
  onConfirm?.();
}

async function submitDirectMessage(): Promise<void> {
  const employee = sessionCatalogStore.employee;
  const companyId = sessionCatalogStore.sessionInformation?.company?.id ?? null;
  const recipientId = appDialogsStore.directMessageRecipientId;
  const title = appDialogsStore.directMessageTitle.trim();
  const body = appDialogsStore.directMessageBody.trim();

  directMessageTitleError.value = "";
  directMessageBodyError.value = "";

  if (!title) {
    directMessageTitleError.value = t("wkt.client.common.validation-utils.required_field");
  }

  if (!body) {
    directMessageBodyError.value = t("wkt.client.common.validation-utils.required_field");
  }

  if (!employee || recipientId == null || !title || !body) {
    return;
  }

  directMessageSubmitting.value = true;

  const payload = {
    senderId: employee.id ?? null,
    companyId,
    title,
    body,
    actionType: 0,
    type: 3,
    employees: String(recipientId),
    creationDate: new Date().toISOString(),
    isPrivateMessage: true,
    sticky: false,
    enabled: true,
  };

  try {
    await new Promise<void>((resolve, reject) => {
      const originalMessageId = appDialogsStore.directMessageOriginalMessageId;
      if (originalMessageId != null) {
        homeApi.replyMessage(originalMessageId, payload, () => resolve(), reject);
        return;
      }

      homeApi.publishMessage(payload, () => resolve(), reject);
    });

    closeDirectMessage();
    showAlert({
      text: t("wkt.client.application.message_sent"),
      color: "success",
    });
  } catch (error: any) {
    showAlert({
      text:
        error?.response?.data?.message ||
        error?.message ||
        t("wkt.client.application.send-incidence-error"),
      color: "error",
    });
  } finally {
    directMessageSubmitting.value = false;
  }
}

function openBootstrapFeatureMessagesIfNeeded(): void {
  const sessionInformation = sessionCatalogStore.sessionInformation;
  const employee = sessionCatalogStore.employee;
  const messages = sessionInformation?.featureMessages ?? [];

  if (!employee || !Array.isArray(messages) || messages.length === 0) {
    return;
  }

  const lastFeatureMessage = messages[messages.length - 1];
  if (!lastFeatureMessage?.id || employee.lastClientMessageId === lastFeatureMessage.id) {
    return;
  }

  appDialogsStore.openFeatureMessages(messages);
}

async function acceptCurrentPendingTerm(): Promise<void> {
  const currentTerm = currentPendingTerm.value;
  if (!currentTerm?.id) {
    return;
  }

  appDialogsStore.setPendingTermsSubmitting(true);

  try {
    await new Promise<void>((resolve, reject) => {
      userApi.acceptTerm(currentTerm.id, () => resolve(), reject);
    });

    if (hasNextPendingTerm.value) {
      appDialogsStore.nextPendingTerm();
      return;
    }

    if (sessionCatalogStore.sessionInformation?.pendingTerms) {
      sessionCatalogStore.sessionInformation.pendingTerms = [];
    }

    appDialogsStore.closePendingTerms();
    openBootstrapFeatureMessagesIfNeeded();
  } catch (_) {
    showAlert({
      text: t("wkt.client.application.show-tutorial-error"),
      color: "error",
    });
  } finally {
    appDialogsStore.setPendingTermsSubmitting(false);
  }
}

function rejectPendingTerms(): void {
  appDialogsStore.closePendingTerms();
  openLogoutDialog();
}

async function submitIncidence(): Promise<void> {
  const message = appDialogsStore.incidenceDraft.trim();
  if (!message) {
    incidenceError.value = t(
      "wkt.client.application.send-incidence-window.required",
    );
    return;
  }

  appDialogsStore.setIncidenceSubmitting(true);
  incidenceError.value = "";

  try {
    await new Promise<boolean>((resolve, reject) => {
      userApi.reportIncidence(message, resolve, reject);
    });

    showAlert({
      text: t("wkt.client.application.send-incidence"),
      color: "success",
    });
    appDialogsStore.closeIncidence();
  } catch (_) {
    showAlert({
      text: t("wkt.client.application.send-incidence-error"),
      color: "error",
    });
  } finally {
    appDialogsStore.setIncidenceSubmitting(false);
  }
}

async function closeFeatureMessages(): Promise<void> {
  const lastFeatureMessage =
    appDialogsStore.featureMessages[appDialogsStore.featureMessages.length - 1];
  const employee = sessionCatalogStore.employee;

  try {
    if (
      lastFeatureMessage?.id != null &&
      employee &&
      employee.lastClientMessageId !== lastFeatureMessage.id
    ) {
      await new Promise<void>((resolve, reject) => {
        userApi.updateLastFeatureMessageId(
          CLIENT_MODULE,
          lastFeatureMessage.id,
          () => resolve(),
          reject,
        );
      });
      employee.lastClientMessageId = lastFeatureMessage.id;
    }
  } catch (_) {
    showAlert({
      text: t("wkt.client.application.show-tutorial-error"),
      color: "error",
    });
  } finally {
    appDialogsStore.closeFeatureMessages();
  }
}

watch(
  () => appDialogsStore.confirmDialogOpen,
  (open) => {
    if (!open) {
      confirmDialogInput.value = "";
      confirmDialogInputError.value = "";
    }
  },
);

watch(
  () => appDialogsStore.directMessageOpen,
  (open) => {
    if (!open) {
      directMessageTitleError.value = "";
      directMessageBodyError.value = "";
      directMessageSubmitting.value = false;
    }
  },
);
</script>

<style>
.app-global-dialog.fixNotchPaddingTop {
  padding-top: 0 !important;
}

.wkt-push-toast-message {
  width: min(420px, calc(100vw - 24px));
  border: 1px solid rgba(197, 126, 0, 0.22);
  border-radius: 10px;
  box-shadow: 0 14px 34px rgba(18, 49, 79, 0.18);
}

.wkt-push-toast-message .p-toast-message-content {
  align-items: flex-start;
  padding: 14px 16px;
}

.wkt-push-toast-message .p-toast-summary {
  color: #12314f;
  font-size: 0.95rem;
  font-weight: 700;
}

.wkt-push-toast-message .p-toast-detail {
  margin-top: 4px;
  color: #3f5166;
  font-size: 0.88rem;
  line-height: 1.35;
}

.app-global-dialog .p-dialog-header {
  align-items: flex-start;
}

.app-global-dialog__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.app-global-dialog__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #12314f;
}

.app-global-dialog__subtitle {
  margin: 0;
  font-size: 0.86rem;
  color: #65758a;
}

.app-global-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.app-global-dialog__copy {
  margin: 0;
  color: #3f5166;
}

.app-global-dialog__textarea {
  width: 100%;
  min-height: 9rem;
}

.app-global-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.app-global-dialog__input {
  width: 100%;
}

.app-global-dialog__recipient {
  display: flex;
  align-items: center;
  gap: 12px;
}

.app-global-dialog__recipient-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  background: #e6edf5;
}

.app-global-dialog__recipient-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #3f5166;
}

.app-global-dialog__recipient-copy strong {
  color: #12314f;
}

.app-global-dialog__recipient-copy span {
  font-size: 0.86rem;
  color: #65758a;
}

.app-global-dialog__confirm-token {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.app-global-dialog__confirm-label {
  font-size: 0.86rem;
  color: #65758a;
}

.app-global-dialog__article {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.app-global-dialog__article-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #12314f;
}

.app-global-dialog__html {
  color: #3f5166;
  line-height: 1.55;
}

.app-global-dialog__html p:first-child {
  margin-top: 0;
}

.app-global-dialog__html p:last-child {
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .app-global-dialog.p-dialog {
    width: min(36rem, calc(100vw - 1.5rem)) !important;
    height: auto !important;
    max-height: calc(
      100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 1.5rem
    ) !important;
    margin: calc(env(safe-area-inset-top, 0px) + 0.75rem) 0.75rem
      calc(env(safe-area-inset-bottom, 0px) + 0.75rem) !important;
    border-radius: 24px !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .app-global-dialog.p-dialog .p-dialog-header {
    padding: 1.25rem 1.25rem 0.85rem;
    flex-shrink: 0;
  }

  .app-global-dialog.p-dialog .p-dialog-content {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0 1.25rem 1.1rem;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .app-global-dialog.p-dialog .p-dialog-footer {
    position: static !important;
    left: auto !important;
    bottom: auto !important;
    width: auto !important;
    flex-shrink: 0;
    padding: 0.85rem 1.25rem
      calc(env(safe-area-inset-bottom, 0px) + 1rem);
    background: #ffffff;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .app-global-dialog--direct-message.p-dialog {
    height: min(
      34rem,
      calc(
        100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 1.5rem
      )
    ) !important;
  }

  .app-global-dialog--direct-message.p-dialog .p-dialog-content {
    padding-bottom: 1.25rem;
  }

  .app-global-dialog--direct-message .app-global-dialog__body {
    min-height: 100%;
    padding-bottom: 0.25rem;
  }

  .app-global-dialog--direct-message .app-global-dialog__textarea {
    min-height: 10rem;
  }

  .app-global-dialog__header {
    gap: 8px;
  }

  .app-global-dialog__title {
    font-size: 1.2rem;
    line-height: 1.2;
  }

  .app-global-dialog__subtitle,
  .app-global-dialog__copy,
  .app-global-dialog__confirm-label,
  .app-global-dialog__recipient-copy span,
  .app-global-dialog__html {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .app-global-dialog--fullscreen.p-dialog {
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100dvh !important;
    max-height: 100dvh !important;
    margin: 0 !important;
    border-radius: 0 !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .app-global-dialog--fullscreen.p-dialog .p-dialog-header {
    padding: calc(env(safe-area-inset-top, 0px) + 1rem) 1.25rem 0.9rem;
  }

  .app-global-dialog--fullscreen.p-dialog .p-dialog-content {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0 1.25rem 1rem;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .app-global-dialog--fullscreen.p-dialog .p-dialog-footer {
    position: static;
    width: auto;
    padding: 0.85rem 1.25rem
      calc(env(safe-area-inset-bottom, 0px) + 1rem);
    background: #ffffff;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .app-global-dialog--fullscreen .app-global-dialog__html {
    padding-bottom: 0.25rem;
  }
}
</style>
